import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Bell, BarChart3, Calculator, User, LogOut,
  Rocket, Menu, X
} from 'lucide-react';
import { useAuthContext } from '../../context/AuthContext';
import { useAppContext } from '../../context/AppContext';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/tiers', label: 'Sizing', icon: Calculator },
  { path: '/alerts', label: 'Alerts', icon: Bell },
  { path: '/trades', label: 'Backtest', icon: BarChart3 },
];

const Sidebar = () => {
  const auth = useAuthContext();
  const app = useAppContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  useEffect(() => { closeMobile(); }, [location.pathname, closeMobile]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleAccountClick = () => {
    closeMobile();
    if (auth.isSubscribed) {
      app.setShowAccountModal(true);
    } else {
      app.setShowTrialOfferModal(true);
    }
  };

  const handleLogout = () => {
    closeMobile();
    app.setShowLogoutConfirmModal(true);
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    closeMobile();
    navigate('/dashboard');
  };

  const sidebarContent = (
    <>
      <div className="sidebar__top">
        <a href="/dashboard" className="sidebar__logo" onClick={handleLogoClick}>
          <img src="/logo.png" alt="AlphaNQ" className="sidebar__logo-img" />
        </a>
      </div>

      <nav className="sidebar__nav">
        <span className="sidebar__nav-label">Menu</span>
        {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
          <Link
            key={path}
            to={path}
            className={`sidebar__link ${location.pathname === path ? 'sidebar__link--active' : ''}`}
            onClick={closeMobile}
          >
            <Icon size={18} />
            <span>{label}</span>
          </Link>
        ))}
      </nav>

      <div className="sidebar__bottom">
        {auth.isLoggedIn && (
          <button className="sidebar__link sidebar__link--accent" onClick={handleAccountClick}>
            {auth.isSubscribed ? (
              <><User size={18} /><span>Account</span></>
            ) : (
              <><Rocket size={18} /><span>{app.hasCompletedTrial ? 'Subscribe' : 'Start Trial'}</span></>
            )}
          </button>
        )}
        <button className="sidebar__link sidebar__link--muted" onClick={handleLogout}>
          <LogOut size={18} />
          <span>Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="sidebar sidebar--desktop">
        {sidebarContent}
      </aside>

      {/* Mobile top bar */}
      <div className="sidebar-mobile-bar">
        <a href="/dashboard" className="sidebar__logo" onClick={handleLogoClick}>
          <img src="/logo.png" alt="AlphaNQ" className="sidebar__logo-img sidebar__logo-img--mobile" />
        </a>
        <button
          className="sidebar-mobile-bar__toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && <div className="sidebar-mobile__backdrop" onClick={closeMobile} />}
      <aside className={`sidebar sidebar--mobile ${mobileOpen ? 'sidebar--mobile-open' : ''}`}>
        {sidebarContent}
      </aside>
    </>
  );
};

export default Sidebar;
